// src/components/lesson/LessonShell.jsx

import React from "react";
import { useNavigate } from "react-router-dom";

import LessonProgressBar from "./LessonProgressBar";

export default function LessonShell({
  children,

  /*
   * Progress
   */
  showProgress = true,
  progressStep = 1,
  progressTotal = 1,

  /*
   * Optional page label.
   */
  sectionLabel = "",

  /*
   * Header
   */
  showHeader = false,
  logoSrc = "",
  logoAlt = "Gwin",

  /*
   * Back navigation
   */
  showBack = true,
  backLabel = "Back",
  backTo,
  onBack,

  /*
   * Next navigation
   */
  showNext = true,
  nextLabel = "Next",
  nextTo,
  onNext,
  nextDisabled = false,

  /*
   * Styling hooks
   */
  className = "",
  contentClassName = "",
  contentAlign = "top",
}) {
  const navigate = useNavigate();

  const canGoNext = !nextDisabled && (!!onNext || !!nextTo);

  const safeProgressTotal = Math.max(Number(progressTotal) || 1, 1);

  const safeProgressStep = Math.min(
    Math.max(Number(progressStep) || 1, 1),
    safeProgressTotal
  );

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (backTo) {
      navigate(backTo);
      return;
    }

    navigate(-1);
  };

  const handleNext = () => {
    if (!canGoNext) {
      return;
    }

    if (onNext) {
      onNext();
      return;
    }

    if (nextTo) {
      navigate(nextTo);
    }
  };

  const contentAlignmentClass =
    contentAlign === "center" ? "flex flex-1 flex-col justify-center" : "";

  return (
    <div
      className={[
        "gwin-mobile-page",
        "relative",
        "min-h-[100dvh]",
        "bg-white",
        "text-[#111111]",
        className,
      ].join(" ")}
    >
      {showHeader && logoSrc && (
        <header className="border-b border-[#D9D9D9] bg-white">
          <div className="mx-auto flex h-[78px] w-full max-w-[760px] items-center px-4 sm:h-[88px] sm:px-6 md:px-8">
            <img
              src={logoSrc}
              alt={logoAlt}
              className="h-auto w-[118px] max-w-full object-contain object-left sm:w-[138px]"
              loading="eager"
              decoding="async"
              draggable="false"
            />
          </div>
        </header>
      )}

      <main
        className={[
          "gwin-mobile-content",
          "flex",
          "min-h-[100dvh]",
          "w-full",
          "flex-col",
          "px-4",
          "pt-6",
          "pb-[calc(132px+env(safe-area-inset-bottom,0px))]",
          "sm:px-6",
          "sm:pt-8",
          "md:px-8",
          "md:pt-10",
          contentClassName,
        ].join(" ")}
      >
        {showProgress && (
          <div className="mx-auto w-full max-w-[560px]">
            <p className="mb-3 text-center text-[12px] font-medium text-[#707070] sm:text-[13px]">
              {safeProgressStep} of {safeProgressTotal}
            </p>

            <LessonProgressBar
              currentStep={safeProgressStep}
              totalSteps={safeProgressTotal}
              className="mb-0"
            />
          </div>
        )}

        {!!sectionLabel && (
          <p className="mt-8 text-center text-[14px] font-extrabold uppercase tracking-[0.01em] text-[#FF914D] sm:mt-10 sm:text-[16px]">
            {sectionLabel}
          </p>
        )}

        <div
          className={[
            "w-full",
            "max-w-full",
            contentAlignmentClass,
            showProgress ? "mt-8 sm:mt-10" : "",
          ].join(" ")}
        >
          {children}
        </div>
      </main>

      {(showBack || showNext) && (
        <footer
          className="fixed inset-x-0 bottom-0 z-40 border-t border-[#EFEFEF] bg-white"
          style={{
            paddingBottom: "max(14px, env(safe-area-inset-bottom, 0px))",
          }}
        >
          <div className="mx-auto flex w-full max-w-[620px] items-center justify-between gap-3 px-4 pt-3 sm:gap-6 sm:px-6 sm:pt-4">
            {showBack ? (
              <button
                type="button"
                onClick={handleBack}
                className="
                  flex
                  h-[54px]
                  min-w-0
                  flex-1
                  items-center
                  justify-center
                  rounded-[10px]
                  bg-[#B8B8B8]
                  px-3
                  text-[14px]
                  font-bold
                  text-white
                  transition
                  hover:bg-[#A9A9A9]
                  active:scale-[0.98]
                  sm:h-[58px]
                  sm:px-5
                  sm:text-[16px]
                "
              >
                <span
                  className="mr-auto text-[22px] font-normal leading-none sm:text-[25px]"
                  aria-hidden="true"
                >
                  ←
                </span>

                <span className="truncate px-2">{backLabel}</span>

                <span className="ml-auto opacity-0" aria-hidden="true">
                  ←
                </span>
              </button>
            ) : (
              <div className="h-[54px] min-w-0 flex-1 sm:h-[58px]" />
            )}

            {showNext && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext}
                className={[
                  "flex",
                  "h-[54px]",
                  "min-w-0",
                  "flex-1",
                  "items-center",
                  "justify-center",
                  "rounded-[10px]",
                  "px-3",
                  "text-[14px]",
                  "font-bold",
                  "transition",
                  "active:scale-[0.98]",
                  "sm:h-[58px]",
                  "sm:px-5",
                  "sm:text-[16px]",
                  canGoNext
                    ? "bg-[#FF914D] text-white hover:bg-[#F5833F]"
                    : "cursor-not-allowed bg-[#E1E1E1] text-[#A6A6A6]",
                ].join(" ")}
              >
                <span className="mr-auto opacity-0" aria-hidden="true">
                  →
                </span>

                <span className="truncate px-2">{nextLabel}</span>

                <span
                  className="ml-auto text-[22px] font-normal leading-none sm:text-[25px]"
                  aria-hidden="true"
                >
                  →
                </span>
              </button>
            )}
          </div>
        </footer>
      )}
    </div>
  );
}